import type { ObjectId } from "@bbj-types/utils";
import { TextClamper } from "@data-display/TextClamper";
import { type Theme, Typography, useMediaQuery } from "@mui/material";
import { insertTextAtBeginningOfHtml } from "@root/utils/string/includeTextIntoHtml";
import dompurify from "isomorphic-dompurify";
import { type MouseEvent, type MouseEventHandler, useState } from "react";
import { ActionCommentDialog } from "./commentDialog";
import { communicationActionStyle } from "./styles";

export enum OriginText {
  EMAIL = "email",
  SMS = "sms",
  COMMENT = "comment",
}

interface ITextClamperTypographyProps {
  text: string | undefined;
  origin: OriginText;
}

interface IActionContentTextProps {
  contents: ITextClamperTypographyProps[];
  /** Lets the image link of a lightened comment or email open the full content in a dialog */
  actionId?: ObjectId;
}

/** Attribute carried by the link the API substitutes for inline images in list comments and emails */
export const ACTION_IMAGE_LINK_ATTRIBUTE = "data-action-image";
const IMAGE_LINK_SELECTOR = `[${ACTION_IMAGE_LINK_ATTRIBUTE}]`;
const imageLinkStyle = { [`& a${IMAGE_LINK_SELECTOR}`]: { color: "primary.main", fontWeight: 600 } };

const TextClamperTypography = ({
  text,
  origin,
  onClick,
}: ITextClamperTypographyProps & { onClick?: MouseEventHandler<HTMLElement> }) => {
  if (!text) {
    return null;
  }
  switch (origin) {
    case OriginText.EMAIL:
      return (
        <Typography
          data-hj-suppress="true"
          variant="caption"
          sx={{ [`& *:not(a${IMAGE_LINK_SELECTOR})`]: communicationActionStyle, ...imageLinkStyle }}
          onClick={onClick}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: Required for HTML content rendering
          dangerouslySetInnerHTML={{
            __html: insertTextAtBeginningOfHtml("Contenu :", dompurify.sanitize(text)),
          }}
        />
      );
    case OriginText.SMS:
      return (
        <Typography data-hj-suppress="true" variant="caption" sx={communicationActionStyle}>
          Contenu : {text}
        </Typography>
      );
    case OriginText.COMMENT:
      return (
        <Typography
          data-hj-suppress="true"
          variant="caption"
          sx={{ margin: 0, marginBottom: 1, ...imageLinkStyle }}
          onClick={onClick}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
          dangerouslySetInnerHTML={{ __html: dompurify.sanitize(text) }}
        />
      );
  }
};

export const ActionContentText = ({ contents, actionId }: IActionContentTextProps) => {
  const maxTextLine = useMediaQuery((theme: Theme) => theme.breakpoints.up("desktop")) ? 3 : 0;
  const [dialogOrigin, setDialogOrigin] = useState<OriginText>();

  const handleImageLinkClick = (event: MouseEvent<HTMLElement>, origin: OriginText) => {
    if (!(event.target instanceof Element) || !event.target.closest(IMAGE_LINK_SELECTOR)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (actionId) {
      setDialogOrigin(origin);
    }
  };

  return (
    <>
      <TextClamper
        stackProps={{ gap: 0 }}
        text={
          <div>
            {contents.map((content, index) => (
              <TextClamperTypography
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                key={index}
                text={content.text}
                origin={content.origin}
                onClick={
                  content.origin === OriginText.SMS ? undefined : (event) => handleImageLinkClick(event, content.origin)
                }
              />
            ))}
          </div>
        }
        clampLabel={{
          open: "Voir plus",
          close: "Voir moins",
        }}
        maxLine={maxTextLine}
      />
      {actionId && dialogOrigin && (
        <ActionCommentDialog
          actionId={actionId}
          isEmail={dialogOrigin === OriginText.EMAIL}
          onClose={() => setDialogOrigin(undefined)}
        />
      )}
    </>
  );
};
